/*
 *  Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import ReactGA from "react-ga4";
import ApiClient from "../apiClient";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromInsurance,
  sanitizeAddressData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toInsurance,
} from "../formServiceFunctions";
import { getProductData } from "../productService";
import _forEach from "lodash/forEach";
import _set from "lodash/set";
import _get from "lodash/get";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";
import { AbstractCase } from "../../types/Entities/AbstractCase";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_ACCUSATION]: {
      traffic: ["accusation"],
    },
    [applicationStatuses.APPLICATION_STATUS_AUTHORITY_NOTIFICATION]: {
      traffic: ["authorityNotification"],
    },
    //-- speeding
    [applicationStatuses.APPLICATION_STATUS_LOCALITY]: {
      traffic: ["locality"],
    },
    [applicationStatuses.APPLICATION_STATUS_SPEEDING]: {
      traffic: ["speeding"],
    },
    //-- traffic light
    [applicationStatuses.APPLICATION_STATUS_ROAD_USERS_ENDANGERED]: {
      traffic: ["roadUsersEndangered"],
    },
    [applicationStatuses.APPLICATION_STATUS_DAMAGE]: {
      traffic: ["damage"],
    },
    [applicationStatuses.APPLICATION_STATUS_RED_LIGHT]: {
      traffic: ["redLight"],
    },
    //-- distance
    [applicationStatuses.APPLICATION_STATUS_SPEED]: {
      traffic: ["speed"],
    },
    [applicationStatuses.APPLICATION_STATUS_DISTANCE]: {
      traffic: ["distance"],
    },
    //-- other
    [applicationStatuses.APPLICATION_STATUS_ACCUSATION_OTHER]: {
      traffic: ["accusationOther"],
    },
    [applicationStatuses.APPLICATION_STATUS_VEHICLE]: {
      traffic: ["vehicle"],
    },
    [applicationStatuses.APPLICATION_STATUS_DRIVERS_LICENSE_PERIOD]: {
      traffic: ["driversLicensePeriod"],
    },
    [applicationStatuses.APPLICATION_STATUS_FLENSBURG_POINTS]: {
      traffic: ["flensburgPoints"],
    },
    [applicationStatuses.APPLICATION_STATUS_FLASHED]: {
      traffic: ["flashed"],
    },
    [applicationStatuses.APPLICATION_STATUS_DRIVERS_LICENSE_INDISPENSABLE]: {
      traffic: ["driversLicenseIndispensable"],
    },
    [applicationStatuses.APPLICATION_STATUS_LEGAL_INSURANCE]: {
      traffic: ["legalExpenseInsurance"],
      insurance: ["insurance", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "insurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL_DATA]: {
      client: ["gender", "givenName", "familyName", "birthDate"],

      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],

      customer: ["telephone", "contactTime"],

      traffic: ["fineNoticeReceivedDate"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENT_CHOICE]: {
      traffic: ["authorityNotification", "fineNoticeReceivedDate"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      traffic: ["powerOfAttorney"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      traffic: [],
    },
  });

  conversionFields = [
    {
      path: "fineNoticeReceivedDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "client.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "insurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "insurance.insurance",
      convertFrom: fromInsurance,
      convertTo: toInsurance,
    },
  ];

  statusFunctions: { [k in ApplicationStatus]?: () => void } = {
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: () => {
      ReactGA.event({
        category: "Document Upload",
        action: "Traffic Documents uploaded",
      });
    },
  };

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product: traffic,
    customer,
    updateMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};
    const convertedData = this.handleProductFields(formData);

    if (_has(fields, "customer")) {
      const customerData = _pick(convertedData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client")) {
      let clientData = _pick(convertedData.client, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: traffic.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      let addressData = _pick(convertedData.client.residenceAddress, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: traffic.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "insurance")) {
      let insuranceData = _pick(convertedData.insurance, fields["insurance"]);
      await ApiClient.put("/customer_insurances/" + traffic.insurance.id, { body: JSON.stringify(insuranceData) });
    }

    let trafficData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      trafficData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, "traffic")) {
      trafficData = _merge(trafficData, _pick(convertedData, fields.traffic));
    }
    await updateMutation.mutateAsync({
      uri: getProductData(traffic.name, "apiUri"),
      id: traffic.id,
      data: trafficData,
    });
  };

  handleProductFields = (data: FormServiceSaveProps<ApplicationStatus>["formData"]) => {
    let resultData = _merge({}, data);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertTo } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertTo(_get(resultData, path)));
      }
    });
    return resultData;
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_ACCUSATION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_AUTHORITY_NOTIFICATION]);

    switch (formData.accusation) {
      case "speeding":
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_LOCALITY]);
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SPEEDING]);
        break;
      case "red_light":
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_ROAD_USERS_ENDANGERED]);
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DAMAGE]);
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_RED_LIGHT]);
        break;
      case "distance":
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SPEED]);
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DISTANCE]);
        break;
      case "other":
        availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_ACCUSATION_OTHER]);
        if (formData.accusationOther === "mobile_phone") {
          availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_VEHICLE]);
        }
        break;
      default:
        break;
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DRIVERS_LICENSE_PERIOD]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_FLENSBURG_POINTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_FLASHED]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DRIVERS_LICENSE_INDISPENSABLE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_LEGAL_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL_DATA]);

    if (product.authorityNotification === "no_notice") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENT_CHOICE]);
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    return availableSteps;
  };
}

export default new FormService();
